import { interfaceError } from '../../i18n/errorMessages';
import { localizeSystemText } from '../../i18n/messages';
import LocalizedMessage from '../common/LocalizedMessage';
import React, { useEffect, useRef, useState } from "react";
import TeacherAccessService from "../../services/TeacherAccessService";
import PropTypes from "prop-types";
import { DEFAULT_LESSON_TYPES_SETTING, getSelectableLessonTypes } from "../common/lessonTypes";
import DateField from "../common/DateField";
import SearchableSelect from "../common/SearchableSelect";
import { useTranslation } from 'react-i18next';

const WEEKDAY_IDS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const actionTitles = {
  "lesson-details": "Zmień dane tych zajęć",
  "absence-report": "Zgłoszenie nieobecności",
  "lesson-cancellation": "Odwołaj lekcję",
  "undo-cancellation": "Cofnij odwołanie lekcji",
  "student-list": "Zarządzaj listą uczniów",
};

const getDefaultReportedAtValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
};

const formatPersonName = person => (
  [person.firstName, person.lastName].filter(Boolean).join(" ") || person.id
);

const formatLessonDateDayOfWeek = (date) => {
  const dateInputMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const dayOfWeek = dateInputMatch
    ? new Date(Date.UTC(
      Number(dateInputMatch[1]),
      Number(dateInputMatch[2]) - 1,
      Number(dateInputMatch[3]),
    )).getUTCDay()
    : new Date(date).getDay();

  return Number.isNaN(dayOfWeek)
    ? null
    : WEEKDAY_IDS[dayOfWeek];
};

function AttendanceCommandModals({
  activeAction,
  initialAbsenceStudentId,
  viewModel,
  teachers,
  lessonTypes,
  canEditLessonComment,
  canEditLessonDetails,
  canManageStudents,
  serverStudentSearch,
  allStudents,
  classStudents,
  canUseAdministrativeCommands,
  administrativeCommandBlockReason,
  canUndoWholeLessonCancellation,
  onClose,
  onUpdateLessonDetails,
  onAddStudent,
  onRemoveStudent,
  onReportAbsence,
  onCancelWholeLesson,
  onUndoWholeLessonCancellation,
}) {
  const { t: tr } = useTranslation();
  const [commandError, setCommandError] = useState("");
  const [lessonDetailsDraft, setLessonDetailsDraft] = useState(viewModel.lessonDetails);
  const [absenceDraft, setAbsenceDraft] = useState({
    studentId: "",
    reportedAt: getDefaultReportedAtValue(),
    note: "",
  });
  const [studentToAdd, setStudentToAdd] = useState("");
  const [studentToRemove, setStudentToRemove] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [candidateQuery, setCandidateQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candidateCursor, setCandidateCursor] = useState(null);
  const candidateGeneration = useRef(0);
  const loadCandidates = async (cursor, generation) => {
    try {
      const result = await TeacherAccessService.getCandidates(viewModel.schoolClass.id, candidateQuery, cursor);
      if (candidateGeneration.current !== generation) return;
      setCandidates(current => cursor ? [...current, ...result.items] : result.items);
      setCandidateCursor(result.cursor);
    } catch (error) {
      if (candidateGeneration.current === generation) setCommandError(interfaceError(error));
    }
  };
  useEffect(() => {
    const generation = ++candidateGeneration.current;
    setCandidates([]);
    setCandidateCursor(null);
    if (!serverStudentSearch || !canManageStudents || activeAction !== 'student-list') return undefined;
    const timeout = setTimeout(() => loadCandidates('', generation), 200);
    return () => { clearTimeout(timeout); candidateGeneration.current += 1; };
  }, [serverStudentSearch, canManageStudents, activeAction, viewModel.schoolClass.id, candidateQuery, classStudents]);

  useEffect(() => {
    if (!activeAction) return;
    setCommandError("");
    setLessonDetailsDraft(viewModel.lessonDetails);
    setAbsenceDraft({
      studentId: initialAbsenceStudentId,
      reportedAt: getDefaultReportedAtValue(),
      note: "",
    });
    setStudentToAdd("");
    setStudentToRemove(null);
    setCancellationReason("");
  }, [activeAction, initialAbsenceStudentId, viewModel.lessonDetails, viewModel.scheduledLesson.id]);

  if (!activeAction) return null;

  const resetAndClose = () => {
    setCommandError("");
    setStudentToRemove(null);
    onClose();
  };

  const handleCommand = (command, fallbackMessage) => {
    setCommandError("");
    command()
      .then(resetAndClose)
      .catch(error => setCommandError(interfaceError(error, interfaceError(fallbackMessage).key)));
  };

  const handleStudentCommand = (command, fallbackMessage) => {
    setCommandError("");
    command()
      .catch(error => setCommandError(interfaceError(error, interfaceError(fallbackMessage).key)));
  };

  const activeTeachers = teachers.filter(teacher => !teacher.archived && !teacher.deleted);

  const renderLessonDetailsForm = () => (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        handleCommand(
          () => onUpdateLessonDetails(lessonDetailsDraft),
          tr("manage:couldNotSaveLessonDetails_7"),
        );
      }}
    >
      <p className="text-muted">{tr("manage:singleLessonTypeScope")}</p>
      <div className="attendance-page__field">
        <span>{tr("manage:lessonTypeLabel")}</span>
        <select
          aria-label={tr("manage:lessonTypeLabel")}
          className="form-control mb-3"
          disabled={!canEditLessonDetails}
          value={lessonDetailsDraft.classTypeId || ""}
          onChange={event => setLessonDetailsDraft({ ...lessonDetailsDraft, classTypeId: event.target.value })}
        >
          {getSelectableLessonTypes(lessonTypes, viewModel.lessonDetails.classTypeId).map(type => (
            <option key={type.classTypeId} value={type.classTypeId}>{type.name}</option>
          ))}
        </select>
      </div>
      <div className="attendance-page__field">
        <span>{tr("manage:date_8")}</span>
        <DateField
          aria-label={tr("manage:date_8")}
          className="form-control mb-3"
          type="date"
          disabled={!canEditLessonDetails}
          value={lessonDetailsDraft.date || ""}
          onChange={event => setLessonDetailsDraft({
            ...lessonDetailsDraft,
            date: event.target.value,
            dayOfWeek: formatLessonDateDayOfWeek(event.target.value),
          })}
        />
      </div>
      <div className="attendance-page__field">
        <span>{tr("calendar:time")}</span>
        <input
          aria-label={tr("calendar:time")}
          className="form-control mb-3"
          type="time"
          disabled={!canEditLessonDetails}
          value={lessonDetailsDraft.startTime || ""}
          onChange={event => setLessonDetailsDraft({ ...lessonDetailsDraft, startTime: event.target.value })}
        />
      </div>
      <div className="attendance-page__field">
        <span>{tr("manage:teacher_9")}</span>
        <select
          aria-label={tr("manage:teacher_9")}
          className="form-control mb-3"
          disabled={!canEditLessonDetails}
          value={lessonDetailsDraft.teacherId || ""}
          onChange={event => setLessonDetailsDraft({ ...lessonDetailsDraft, teacherId: event.target.value })}
        >
          {activeTeachers.map(teacher => (
            <option key={teacher.id} value={teacher.id}>{`${teacher.firstName} ${teacher.lastName}`}</option>
          ))}
        </select>
      </div>
      <div className="attendance-page__field">
        <span>{tr("manage:notes_10")}</span>
        <textarea
          aria-label={tr("manage:notes_10")}
          className="form-control mb-3"
          disabled={!canEditLessonComment}
          value={lessonDetailsDraft.comment || ""}
          onChange={event => setLessonDetailsDraft({ ...lessonDetailsDraft, comment: event.target.value })}
        />
      </div>
      <div className="attendance-page__modal-footer">
        <button type="button" className="btn manage-button manage-teal-button" onClick={resetAndClose}>{tr("common:cancel")}</button>
        <button type="submit" className="btn manage-button manage-red-button">{tr("manage:saveChanges_11")}</button>
      </div>
    </form>
  );

  const renderAbsenceForm = () => (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!absenceDraft.studentId) {
          setCommandError(tr("manage:extraSelectAStudent_0"));
          return;
        }
        if (!absenceDraft.reportedAt || Number.isNaN(new Date(absenceDraft.reportedAt).getTime())) {
          setCommandError(tr("manage:enterAValidReportTime_12"));
          return;
        }
        handleCommand(
          () => onReportAbsence({
            studentId: absenceDraft.studentId,
            reportedAt: new Date(absenceDraft.reportedAt).toISOString(),
            note: absenceDraft.note,
          }),
          tr("manage:couldNotSaveTheAbsenceReport_13"),
        );
      }}
    >
      <div className="attendance-page__field">
        <span>{tr("manage:student_14")}</span>
        <select
          aria-label={tr("manage:student_14")}
          className="form-control mb-3"
          value={absenceDraft.studentId}
          onChange={event => setAbsenceDraft({ ...absenceDraft, studentId: event.target.value })}
        >
          <option value="">{tr("manage:selectAStudent_15")}</option>
          {viewModel.participants.map(participant => (
            <option key={participant.id} value={participant.id}>{formatPersonName(participant)}</option>
          ))}
        </select>
      </div>
      <div className="attendance-page__field">
        <span>{tr("manage:whenWeReceivedTheReport_16")}</span>
        <DateField
          aria-label={tr("manage:whenWeReceivedTheReport_16")}
          className="form-control mb-3"
          type="datetime-local"
          value={absenceDraft.reportedAt}
          onChange={event => setAbsenceDraft({ ...absenceDraft, reportedAt: event.target.value })}
        />
      </div>
      <div className="attendance-page__field">
        <span>{tr("manage:note_17")}</span>
        <textarea
          aria-label={tr("manage:note_17")}
          className="form-control mb-3"
          value={absenceDraft.note}
          onChange={event => setAbsenceDraft({ ...absenceDraft, note: event.target.value })}
        />
      </div>
      <div className="attendance-page__modal-footer">
        <button type="button" className="btn manage-button manage-teal-button" onClick={resetAndClose}>{tr("common:cancel")}</button>
        <button type="submit" className="btn manage-button manage-red-button">{tr("manage:saveReport_18")}</button>
      </div>
    </form>
  );

  const renderCancellationForm = () => (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!cancellationReason.trim()) {
          setCommandError(tr("manage:enterAReasonForCancellingTheLesson_19"));
          return;
        }
        handleCommand(
          () => onCancelWholeLesson({ reason: cancellationReason }),
          tr("manage:couldNotCancelTheLesson_20"),
        );
      }}
    >
      <div className="attendance-page__field">
        <span>{tr("manage:reasonForCancellingTheLesson_21")}</span>
        <textarea
          aria-label={tr("manage:reasonForCancellingTheLesson_21")}
          className="form-control mb-3"
          value={cancellationReason}
          onChange={event => setCancellationReason(event.target.value)}
        />
      </div>
      <div className="attendance-page__modal-footer">
        <button type="button" className="btn btn-light" onClick={resetAndClose}>{tr("common:cancel")}</button>
        <button type="submit" className="btn btn-danger">{tr("manage:confirmCancellation_22")}</button>
      </div>
    </form>
  );

  const activeClassStudentIds = classStudents
    .filter(classStudent => classStudent.schoolClassId === viewModel.schoolClass.id)
    .map(classStudent => classStudent.studentId);
  const participantIds = viewModel.participants.map(student => student.id);
  const studentsAvailableToAdd = (serverStudentSearch ? candidates : allStudents)
    .filter(student => !student.deleted && !student.archived)
    .filter(student => !activeClassStudentIds.includes(student.id) && !participantIds.includes(student.id));

  const handleAddStudent = (event) => {
    event.preventDefault();
    if (!studentToAdd) {
      setCommandError(tr("manage:extraSelectAStudentToAdd_1"));
      return;
    }
    handleStudentCommand(
      () => onAddStudent(studentToAdd).then(() => setStudentToAdd("")),
      tr("manage:couldNotAddTheStudentToTheGroup_23"),
    );
  };

  const handleRemoveStudentClick = (student) => {
    const isRecordedInLesson = Boolean(
      viewModel.scheduledLesson.lessonRegister &&
      (viewModel.scheduledLesson.lessonRegister.students || []).some(s => s.id === student.id),
    );
    if (isRecordedInLesson) {
      setStudentToRemove(student);
    } else {
      handleConfirmRemove({ studentId: student.id, removeFromGroup: true, removeFromLesson: false });
    }
  };

  const handleConfirmRemove = ({ studentId, removeFromGroup, removeFromLesson }) => {
    setStudentToRemove(null);
    handleStudentCommand(
      () => onRemoveStudent(studentId, { removeFromGroup, removeFromLesson }),
      tr("manage:couldNotRemoveTheStudentFromTheGroup_24"),
    );
  };

  const renderStudentList = () => {
    if (studentToRemove) {
      const isEnrolledInClass = activeClassStudentIds.includes(studentToRemove.id);
      const isRecordedInLesson = Boolean(
        viewModel.scheduledLesson.lessonRegister &&
        (viewModel.scheduledLesson.lessonRegister.students || []).some(s => s.id === studentToRemove.id),
      );
      const studentName = formatPersonName(studentToRemove);

      if (isRecordedInLesson && !isEnrolledInClass) {
        return (
          <div>
            <p className="mb-3">
              {tr("manage:removeStudentRecordedNotInGroupWarning", {
                name: studentName,
              })}
            </p>
            <div className="attendance-page__modal-footer d-flex flex-column align-items-stretch">
              <button
                type="button"
                className="btn attendance-page__removal-choice-btn mb-2"
                onClick={() => handleConfirmRemove({ studentId: studentToRemove.id, removeFromGroup: false, removeFromLesson: true })}
              >
                {tr("manage:confirmRemoveFromLesson")}
              </button>
              <button
                type="button"
                className="btn manage-button manage-teal-button"
                onClick={() => setStudentToRemove(null)}
              >
                {tr("common:cancel")}
              </button>
            </div>
          </div>
        );
      }

      if (isRecordedInLesson && isEnrolledInClass) {
        return (
          <div>
            <p className="mb-2">
              {tr("manage:removeStudentRecordedWarning", {
                name: studentName,
              })}
            </p>
            <p className="text-muted mb-3">{tr("manage:chooseRemovalScope")}</p>
            <div className="attendance-page__modal-footer d-flex flex-column align-items-stretch">
              <button
                type="button"
                className="btn attendance-page__removal-choice-btn mb-2"
                onClick={() => handleConfirmRemove({ studentId: studentToRemove.id, removeFromGroup: true, removeFromLesson: true })}
              >
                {tr("manage:removeFromLessonAndGroup")}
              </button>
              <button
                type="button"
                className="btn attendance-page__removal-choice-btn mb-2"
                onClick={() => handleConfirmRemove({ studentId: studentToRemove.id, removeFromGroup: false, removeFromLesson: true })}
              >
                {tr("manage:removeFromLessonOnly")}
              </button>
              <button
                type="button"
                className="btn manage-button manage-teal-button"
                onClick={() => setStudentToRemove(null)}
              >
                {tr("common:cancel")}
              </button>
            </div>
          </div>
        );
      }

      return (
        <div>
          <p className="mb-3">
            {tr("manage:confirmRemoveFromGroup", {
              name: studentName,
            })}
          </p>
          <div className="attendance-page__modal-footer d-flex flex-column align-items-stretch">
            <button
              type="button"
              className="btn manage-button manage-red-button mb-2"
              onClick={() => handleConfirmRemove({ studentId: studentToRemove.id, removeFromGroup: true, removeFromLesson: false })}
            >
              {tr("manage:yesRemoveFromTheClass_84")}
            </button>
            <button
              type="button"
              className="btn manage-button manage-teal-button"
              onClick={() => setStudentToRemove(null)}
            >
              {tr("common:cancel")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        {!canManageStudents && <div className="alert alert-warning">{tr("manage:youDoNotHavePermissionToEditTheStudent_25")}</div>}
        <div className="attendance-page__student-list" role="list" aria-label={tr("manage:currentStudents_26")}>
          {viewModel.participants.map(student => (
            <div key={student.id} className="attendance-page__student-list-row" role="listitem">
              <span>{formatPersonName(student)}</span>
              {canManageStudents && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveStudentClick(student)}
                >
                  {tr("manage:remove_27")}
                </button>
              )}
            </div>
          ))}
        </div>
        {canManageStudents && (
          <form onSubmit={handleAddStudent}>
            <SearchableSelect
              name="studentToAdd"
              label={tr("manage:addStudent_28")}
              placeholder={tr("manage:enterTheStudentsFirstOrLastName_29")}
              value={studentToAdd}
              onChange={event => setStudentToAdd(event.target.value)}
              onInputChange={serverStudentSearch ? (value, action) => { if (action.action === 'input-change') setCandidateQuery(value); } : undefined}
              options={studentsAvailableToAdd.map(student => ({ value: student.id, text: formatPersonName(student) }))}
            />
            {serverStudentSearch && candidateCursor && (
              <button type="button" className="btn manage-teal-button" onClick={() => loadCandidates(candidateCursor, candidateGeneration.current)}>
                {tr('manage:teacherExtensions.more')}
              </button>
            )}
            <button type="submit" className="btn btn-outline-success">{tr("manage:addStudent_28")}</button>
          </form>
        )}
        <div className="attendance-page__modal-footer">
          <button type="button" className="btn btn-light" onClick={resetAndClose}>{tr("common:close")}</button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (activeAction === "lesson-details" && !canEditLessonComment && !canEditLessonDetails) {
      return <div className="alert alert-warning">{tr("manage:youDoNotHavePermissionToPerformThisAction_30")}</div>;
    }

    if (
      (activeAction === "absence-report" || activeAction === "lesson-cancellation") &&
      !canUseAdministrativeCommands
    ) {
      const messages = {
        role: tr("manage:youDoNotHavePermissionToPerformThisAction_30"),
        missingLesson: tr("manage:theLessonForThisActionCouldNotBeFound_31"),
        past: tr("manage:thisLessonHasAlreadyStartedOrEndedYouCannot_32"),
        checked: tr("manage:attendanceHasAlreadyBeenRecordedForThisLessonYou_33"),
        cancelled: tr("manage:thisLessonIsCancelledYouCannotReportAStudents_34"),
      };

      return (
        <div className="alert alert-warning">
          {messages[administrativeCommandBlockReason] || tr("manage:thisActionIsNotAvailableRightNow_35")}
        </div>
      );
    }

    if (activeAction === "lesson-details") return renderLessonDetailsForm();
    if (activeAction === "absence-report") return renderAbsenceForm();
    if (activeAction === "lesson-cancellation") return renderCancellationForm();
    if (activeAction === "student-list") return renderStudentList();

    if (activeAction === "undo-cancellation" && canUndoWholeLessonCancellation) {
      return (
        <div>
          <p>{tr("manage:areYouSureYouWantToUndoThisLesson_36")}</p>
          <div className="attendance-page__modal-footer">
            <button type="button" className="btn btn-light" onClick={resetAndClose}>{tr("common:cancel")}</button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => handleCommand(onUndoWholeLessonCancellation, tr("manage:couldNotUndoTheLessonCancellation_37"))}
            >{tr("manage:undoCancellation_4")}</button>
          </div>
        </div>
      );
    }

    return <div className="alert alert-warning">{tr("manage:thisActionIsNotAvailableRightNow_35")}</div>;
  };

  return (
    <div className="attendance-page__modal-placeholder" role="dialog" aria-modal="true">
      <div className="attendance-page__modal-card">
        <button type="button" className="close" aria-label={tr("common:close")} onClick={resetAndClose}>x</button>
        <div className="modal-title">{localizeSystemText(actionTitles[activeAction], tr) || tr("manage:extraLessonAction_2")}</div>
        {commandError && <div className="alert alert-danger" role="alert"><LocalizedMessage isError value={commandError} /></div>}
        {renderContent()}
      </div>
    </div>
  );
}

AttendanceCommandModals.propTypes = {
  initialAbsenceStudentId: PropTypes.string,
  activeAction: PropTypes.string,
  viewModel: PropTypes.object.isRequired,
  teachers: PropTypes.array.isRequired,
  lessonTypes: PropTypes.object,
  canEditLessonComment: PropTypes.bool.isRequired,
  canEditLessonDetails: PropTypes.bool.isRequired,
  canManageStudents: PropTypes.bool.isRequired,
  serverStudentSearch: PropTypes.bool,
  allStudents: PropTypes.array.isRequired,
  classStudents: PropTypes.array.isRequired,
  canUseAdministrativeCommands: PropTypes.bool.isRequired,
  administrativeCommandBlockReason: PropTypes.string,
  canUndoWholeLessonCancellation: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdateLessonDetails: PropTypes.func.isRequired,
  onAddStudent: PropTypes.func.isRequired,
  onRemoveStudent: PropTypes.func.isRequired,
  onReportAbsence: PropTypes.func.isRequired,
  onCancelWholeLesson: PropTypes.func.isRequired,
  onUndoWholeLessonCancellation: PropTypes.func.isRequired,
};

AttendanceCommandModals.defaultProps = {
  initialAbsenceStudentId: "",
  activeAction: "",
  lessonTypes: DEFAULT_LESSON_TYPES_SETTING,
  serverStudentSearch: false,
  administrativeCommandBlockReason: "",
};

export default AttendanceCommandModals;
